/**
 * The mill's face: what it has been asked, and its answer.
 *
 * Unanswered enquiries come first, because the product is the speed of a reply.
 * The mill never sees the buyer — the admin stands between the two sides, and
 * that is the business. Shaped like the buyer's and admin's own screens: a
 * list of cards linking to a `/enquiries/:id` detail route, not a popup or an
 * inline form on the list itself.
 */
import { replyInputSchema, type ReplyAnswer } from '@bjsfabric/shared'
import { useEffect, useState } from 'react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import {
  AttachmentsPanel,
  Button,
  CatalogFabricPanel,
  DeliveryProgress,
  Empty,
  Field,
  Loading,
  Pill,
  Problem,
  leadTime,
  measurement,
  shortDate,
  useRefreshOnFocus,
  type CatalogFabricView,
} from '../ui'

interface EnquiryReply {
  id: string
  answer: ReplyAnswer
  price: string | null
  currency: string | null
  leadTimeDays: number | null
  minQuantity: string | null
  reason: string | null
  comments: string | null
}

interface Enquiry {
  id: string
  sentAt: string
  dueAt: string | null
  adminNote: string | null
  /** Whether *this mill's own* reply is the one the buyer accepted — never who else's was, if not. */
  outcome: 'WON' | 'LOST' | null
  requirement: {
    id: string
    reference: string
    status: string
    /** Meaningful only alongside `outcome: 'WON'` — the mill's own delivery countdown. */
    confirmedAt: string | null
    fabricDescription: string
    catalogFabric?: CatalogFabricView
    widthValue: string | null
    widthUnit: string | null
    gsm: number | null
    shrinkageLength: string | null
    shrinkageWidth: string | null
    spirality: string | null
    repeatRequired: boolean | null
    quantity: string | null
    quantityUnit: string | null
    requiredBy: string | null
    notes: string | null
  }
  reply: EnquiryReply | null
}

const REASONS = [
  ['MACHINE_UNAVAILABLE', 'No machine free'],
  ['RAW_MATERIAL_UNAVAILABLE', 'No yarn'],
  ['TECHNICALLY_NOT_FEASIBLE', 'Cannot make it'],
  ['QUANTITY_TOO_SMALL', 'Quantity too small'],
  ['TIMELINE_TOO_SHORT', 'Not enough time'],
  ['OTHER', 'Other'],
] as const

type StatusFilter = 'ALL' | 'WAITING' | 'ANSWERED' | 'IN_PROGRESS' | 'DELIVERED' | 'CLOSED'

/**
 * The full summary -- reused as a plain card atop the detail page (`href`
 * omitted) and as the tappable list row (`href` given), the same dual-mode
 * pattern as the buyer's own `RequirementCard`.
 */
function EnquiryCard({ enquiry, href }: { enquiry: Enquiry; href?: string }) {
  const requirement = enquiry.requirement
  const locked = requirement.status === 'IN_PROGRESS' || requirement.status === 'CLOSED'

  const body = (
    <>
      <div className="card-head">
        <strong>{requirement.fabricDescription}</strong>
        <span style={{ display: 'flex', gap: 6 }}>
          {enquiry.reply ? (
            <Pill value={enquiry.reply.answer} />
          ) : locked ? (
            // Never actually "new" once it's closed — an unanswered enquiry
            // that stayed unanswered until the requirement locked.
            <Pill value="REJECTED" label="Closed" />
          ) : (
            <Pill value="NEW" />
          )}
          {/* Own-outcome only: never which company actually won when it
              wasn't this one. Skipped when there is no reply at all --
              "Closed" above already says everything a never-answered
              enquiry needs to say. Wording splits on whether it is still
              being worked (IN_PROGRESS) or actually delivered (CLOSED) --
              "closed" for a winning mill means finished, not merely decided. */}
          {enquiry.reply && enquiry.outcome === 'WON' && requirement.status === 'IN_PROGRESS' ? (
            <Pill value="APPROVED" label="Order confirmed" />
          ) : null}
          {enquiry.reply && enquiry.outcome === 'WON' && requirement.status === 'CLOSED' ? (
            <Pill value="APPROVED" label="Delivered" />
          ) : null}
          {enquiry.reply && enquiry.outcome === 'LOST' ? (
            <Pill value="REJECTED" label="Order placed elsewhere" />
          ) : null}
        </span>
      </div>
      <div className="reference">
        {requirement.reference} · asked {shortDate(enquiry.sentAt)}
        {enquiry.dueAt ? ` · wanted by ${shortDate(enquiry.dueAt)}` : ''}
      </div>

      <ul className="specs">
        <li>
          GSM <b>{measurement(requirement.gsm)}</b>
        </li>
        <li>
          Width <b>{measurement(requirement.widthValue, requirement.widthUnit)}</b>
        </li>
        <li>
          Shrink L/W{' '}
          <b>
            {measurement(requirement.shrinkageLength)} / {measurement(requirement.shrinkageWidth)}
          </b>
        </li>
        <li>
          Spirality <b>{measurement(requirement.spirality)}</b>
        </li>
        <li>
          Repeat{' '}
          <b>
            {requirement.repeatRequired === null
              ? '—'
              : requirement.repeatRequired
                ? 'yes'
                : 'no'}
          </b>
        </li>
        <li>
          Quantity <b>{measurement(requirement.quantity, requirement.quantityUnit)}</b>
        </li>
      </ul>

      {requirement.notes ? <p className="field-hint">{requirement.notes}</p> : null}
      {enquiry.adminNote ? <p className="field-hint">Note: {enquiry.adminNote}</p> : null}

      {enquiry.reply ? (
        <ul className="specs" style={{ marginTop: 10 }}>
          <li>
            Your price{' '}
            <b>
              {enquiry.reply.price ?? '—'} {enquiry.reply.currency ?? ''}
            </b>
          </li>
          <li>
            Lead time <b>{leadTime(enquiry.reply.leadTimeDays)}</b>
          </li>
        </ul>
      ) : null}
      {enquiry.reply && enquiry.outcome === 'WON' ? (
        <DeliveryProgress confirmedAt={requirement.confirmedAt} leadTimeDays={enquiry.reply.leadTimeDays} />
      ) : null}

      {!enquiry.reply && locked ? (
        <p className="field-hint">
          {requirement.status === 'IN_PROGRESS'
            ? 'The buyer has already gone with someone else. This is no longer open.'
            : 'This requirement is closed.'}
        </p>
      ) : null}
    </>
  )

  return href ? (
    <Link className="card card-link" to={href}>
      {body}
    </Link>
  ) : (
    <div className="card">{body}</div>
  )
}

export function CompanyInbox() {
  // Before this screen had its own `/enquiries/:id` route, a notification's
  // link pointed here with `?enquiry=<id>` instead. Notifications are never
  // rewritten once written (a notification reports, it doesn't gate -- see
  // the notifications service), so any of those already sitting in someone's
  // list would otherwise land on the plain screen and do nothing, which is
  // indistinguishable from "clicking it didn't work." Forward it to the real
  // route instead. A yet older notification with no id in its link at all
  // (from before per-enquiry targeting existed) has nothing to forward --
  // there's no id to send it to.
  const [legacyParams] = useSearchParams()
  const legacyEnquiryId = legacyParams.get('enquiry')
  if (legacyEnquiryId) return <Navigate to={`/enquiries/${legacyEnquiryId}`} replace />

  return <CompanyInboxList />
}

function CompanyInboxList() {
  const [enquiries, setEnquiries] = useState<Enquiry[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [filter, setFilter] = useState<StatusFilter>('ALL')
  const [drawerOpen, setDrawerOpen] = useState(false)

  const load = async () => {
    try {
      const result = await api.get<{ enquiries: Enquiry[] }>('/enquiries')
      setEnquiries(result.enquiries)
    } catch (problem) {
      setError(problem)
    }
  }

  useEffect(() => {
    void load()
  }, [])
  // A reply of "not asked yet" can become "the buyer already confirmed with
  // someone else" without this mill doing anything at all — catch it when
  // they come back to a tab left open, not only on the load that never saw it.
  useRefreshOnFocus(() => void load())

  if (!enquiries && !error) return <Loading />

  // Closed first: an enquiry the buyer has already gone with someone else on
  // (or that the admin closed outright) is not "waiting for an answer"
  // anymore even if this mill never sent one — it belongs in its own bucket,
  // not mixed into the one that means "you still owe someone a reply".
  const isLocked = (enquiry: Enquiry) =>
    enquiry.requirement.status === 'IN_PROGRESS' || enquiry.requirement.status === 'CLOSED'

  const waiting = enquiries?.filter((enquiry) => !enquiry.reply && !isLocked(enquiry)) ?? []
  const answered = enquiries?.filter((enquiry) => enquiry.reply && !isLocked(enquiry)) ?? []
  // "Closed" used to lump three different things together: a job this mill
  // is actively working on, one it already delivered, and one it simply
  // didn't get. Split so "closed" means what it says for a mill -- delivered
  // or finished -- and an active order doesn't sit in the same bucket as one
  // that never went anywhere.
  const inProgress =
    enquiries?.filter((e) => e.requirement.status === 'IN_PROGRESS' && e.outcome === 'WON') ?? []
  const delivered =
    enquiries?.filter((e) => e.requirement.status === 'CLOSED' && e.outcome === 'WON') ?? []
  const closed = enquiries?.filter((e) => isLocked(e) && e.outcome !== 'WON') ?? []

  const sections: { key: StatusFilter; label: string; items: Enquiry[] }[] = [
    { key: 'WAITING', label: 'Waiting for your answer', items: waiting },
    { key: 'ANSWERED', label: 'Answered', items: answered },
    { key: 'IN_PROGRESS', label: 'In progress', items: inProgress },
    { key: 'DELIVERED', label: 'Delivered', items: delivered },
    { key: 'CLOSED', label: 'Closed', items: closed },
  ]
  const shows = (key: StatusFilter) => filter === 'ALL' || filter === key
  const activeLabel = sections.find((section) => section.key === filter)?.label

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <h1>Enquiries</h1>
        <Button variant="quiet" onClick={() => setDrawerOpen(true)}>
          {activeLabel ?? 'All'} ▾
        </Button>
      </div>
      <p className="lede">Yes, no, or already in stock — with a price and a date.</p>
      <Problem error={error} />

      {drawerOpen ? (
        <>
          {/* An off-canvas drawer, not a permanent side column — the shell
              is a single column sized for one thumb, and a fixed rail would
              eat the width that is deliberately kept for the list itself. */}
          <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} />
          <nav className="drawer">
            <div className="drawer-head">
              <strong>Show</strong>
              <Button variant="quiet" onClick={() => setDrawerOpen(false)}>
                Close
              </Button>
            </div>
            <button
              className={`drawer-item${filter === 'ALL' ? ' drawer-item-active' : ''}`}
              onClick={() => {
                setFilter('ALL')
                setDrawerOpen(false)
              }}
            >
              <span>All</span>
              <span className="drawer-count">{enquiries?.length ?? 0}</span>
            </button>
            {sections.map((section) => (
              <button
                key={section.key}
                className={`drawer-item${filter === section.key ? ' drawer-item-active' : ''}`}
                onClick={() => {
                  setFilter(section.key)
                  setDrawerOpen(false)
                }}
              >
                <span>{section.label}</span>
                <span className="drawer-count">{section.items.length}</span>
              </button>
            ))}
          </nav>
        </>
      ) : null}

      {enquiries?.length === 0 ? <Empty>Nothing has been sent to you yet.</Empty> : null}

      {sections.map((section) =>
        shows(section.key) ? (
          <div key={section.key}>
            {section.items.length > 0 ? <h2>{section.label}</h2> : null}
            {section.items.map((enquiry) => (
              <EnquiryCard key={enquiry.id} enquiry={enquiry} href={`/enquiries/${enquiry.id}`} />
            ))}
          </div>
        ) : null,
      )}

      {filter !== 'ALL' && sections.find((section) => section.key === filter)?.items.length === 0 ? (
        <Empty>Nothing here.</Empty>
      ) : null}
    </>
  )
}

/**
 * One enquiry's own screen -- reached from a list card or straight from a
 * notification's link (`/enquiries/<id>`), same shape as the buyer's and
 * admin's own `/requirements/:id` detail routes.
 */
export function EnquiryDetail() {
  const { id = '' } = useParams()
  const [enquiry, setEnquiry] = useState<Enquiry | null>(null)
  const [error, setError] = useState<unknown>(null)

  const load = async () => {
    try {
      const result = await api.get<{ enquiry: Enquiry }>(`/enquiries/${id}`)
      setEnquiry(result.enquiry)
    } catch (problem) {
      setError(problem)
    }
  }

  useEffect(() => {
    void load()
  }, [id])
  // A reply of "not asked yet" can become "the buyer already confirmed with
  // someone else" without this mill doing anything at all — catch it when
  // they come back to a tab left open. Safe even mid-answer: the reply form
  // below keeps its own typed state across a re-render, since React never
  // remounts it just because the parent's data refreshed.
  useRefreshOnFocus(() => void load())

  if (!enquiry && !error) return <Loading />

  return (
    <>
      <Link to="/" className="reference">
        ← All enquiries
      </Link>
      <h1 style={{ marginTop: 8 }}>{enquiry?.requirement.fabricDescription}</h1>
      <Problem error={error} />

      {enquiry ? (
        <>
          <EnquiryCard enquiry={enquiry} />
          {enquiry.requirement.catalogFabric ? (
            <CatalogFabricPanel fabric={enquiry.requirement.catalogFabric} />
          ) : null}
          {/* Read-only: whatever the buyer (or the admin, at quote time) attached to the
              requirement itself -- most often the photographed job card this mill is
              answering against. `requireVisible` already lets an asked mill see the
              requirement's own fields; this is no different. */}
          <AttachmentsPanel kind="requirement" id={enquiry.requirement.id} />
          <AttachmentsPanel kind="enquiry" id={enquiry.id} canUpload title="Your attachments" />
          <EnquiryAnswer enquiry={enquiry} onSaved={() => void load()} />

          <div className="actions" style={{ marginTop: 16 }}>
            <Link className="button button-quiet" to="/" style={{ lineHeight: '44px' }}>
              Back to list
            </Link>
          </div>
        </>
      ) : null}
    </>
  )
}

/** The reply itself -- read-only once answered, with a way back into the
 *  form, since a mill sometimes needs to revise a price before it's locked. */
function EnquiryAnswer({ enquiry, onSaved }: { enquiry: Enquiry; onSaved: () => void }) {
  const requirement = enquiry.requirement
  // The buyer has either accepted a price built from someone's reply, or the
  // admin closed this without one. Either way, answering now would be an
  // answer nobody is still comparing.
  const locked = requirement.status === 'IN_PROGRESS' || requirement.status === 'CLOSED'
  const [open, setOpen] = useState(!enquiry.reply && !locked)

  // Locked, nothing left to do here: `EnquiryCard` above already carries the
  // current price/lead time (and the "no longer open" message, if this mill
  // never even replied) -- there is no button and no form left to show.
  if (locked) return null

  return (
    <div className="card">
      {enquiry.reply && !open ? (
        <div className="actions" style={{ marginTop: 0 }}>
          <Button variant="quiet" onClick={() => setOpen(true)}>
            Change my answer
          </Button>
        </div>
      ) : null}

      {open ? (
        <ReplyForm
          enquiry={enquiry}
          onSaved={() => {
            setOpen(false)
            onSaved()
          }}
          onCancel={enquiry.reply ? () => setOpen(false) : undefined}
        />
      ) : null}
    </div>
  )
}

function ReplyForm({
  enquiry,
  onSaved,
  onCancel,
}: {
  enquiry: Enquiry
  onSaved: () => void
  onCancel?: () => void
}) {
  const existing = enquiry.reply
  const [answer, setAnswer] = useState<ReplyAnswer>(existing?.answer ?? 'YES')
  const [price, setPrice] = useState(existing?.price ?? '')
  const [currency, setCurrency] = useState(existing?.currency ?? 'INR')
  const [leadTimeDays, setLeadTimeDays] = useState(
    existing?.leadTimeDays === null || existing?.leadTimeDays === undefined
      ? ''
      : String(existing.leadTimeDays),
  )
  const [minQuantity, setMinQuantity] = useState(existing?.minQuantity ?? '')
  const [reason, setReason] = useState(existing?.reason ?? 'MACHINE_UNAVAILABLE')
  const [comments, setComments] = useState(existing?.comments ?? '')
  const [issues, setIssues] = useState<Record<string, string>>({})
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const isNo = answer === 'NO'

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setIssues({})

    const candidate = {
      answer,
      price: isNo || price.trim() === '' ? null : price.trim(),
      currency: isNo || price.trim() === '' ? null : currency,
      leadTimeDays: leadTimeDays.trim() === '' ? null : leadTimeDays.trim(),
      minQuantity: minQuantity.trim() === '' ? null : minQuantity.trim(),
      reason: isNo ? reason : null,
      comments: comments.trim() === '' ? null : comments.trim(),
    }

    // The same rule the server applies: a yes has to be actionable and a no
    // has to be explicable.
    const parsed = replyInputSchema.safeParse(candidate)
    if (!parsed.success) {
      const found: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        const path = issue.path.join('.')
        if (!found[path]) found[path] = issue.message
      }
      setIssues(found)
      return
    }

    setBusy(true)
    try {
      await api.put(`/enquiries/${enquiry.id}/reply`, parsed.data)
      onSaved()
    } catch (problem) {
      setError(problem)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <Problem error={error} />

      <Field label="Your answer">
        <select value={answer} onChange={(event) => setAnswer(event.target.value as ReplyAnswer)}>
          <option value="YES">Yes — I can make it</option>
          <option value="IN_STOCK">I already have it</option>
          <option value="NO">No</option>
        </select>
      </Field>

      {!isNo ? (
        <>
          <div className="row">
            <Field label="Price per unit" error={issues['price']}>
              <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
            </Field>
            <Field label="Currency" error={issues['currency']}>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </Field>
          </div>
          <div className="row">
            <Field
              label="Days to deliver"
              hint={answer === 'IN_STOCK' ? 'Not needed — it is in stock' : undefined}
              error={issues['leadTimeDays']}
            >
              <input
                inputMode="numeric"
                value={leadTimeDays}
                onChange={(e) => setLeadTimeDays(e.target.value)}
              />
            </Field>
            <Field label="Minimum quantity" error={issues['minQuantity']}>
              <input
                inputMode="decimal"
                value={minQuantity}
                onChange={(e) => setMinQuantity(e.target.value)}
              />
            </Field>
          </div>
        </>
      ) : (
        <Field label="Why not" error={issues['reason']}>
          <select value={reason} onChange={(event) => setReason(event.target.value)}>
            {REASONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
      )}

      <Field label="Anything to add" error={issues['comments']}>
        <textarea value={comments} onChange={(event) => setComments(event.target.value)} />
      </Field>

      <div className="actions">
        <Button type="submit" busy={busy}>
          Send my answer
        </Button>
        {onCancel ? (
          <Button type="button" variant="quiet" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  )
}
